import React, { useCallback, useEffect, useState } from 'react';
import { AlertCircle, Ban, Clock, Lock, Shield, User } from 'lucide-react';
import apiService from './services/apiService';

/**
 * Écran d'entrée.
 *
 * La logique de sécurité est inchangée (vérification d'IP, verrouillage
 * temporaire, bannissement, limitation de débit) ; seule la mise en scène
 * a été refaite : un fond de salle obscure, un faisceau de projection et
 * une carte d'accès unique, plutôt qu'un formulaire flottant.
 */
export default function Login({ onLoginSuccess }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);

  /* Sécurité côté serveur */
  const [status, setStatus] = useState(null); // 'allowed' | 'locked' | 'banned'
  const [retryAfter, setRetryAfter] = useState(0);
  const [bannedInfo, setBannedInfo] = useState(null);
  const [rateLimited, setRateLimited] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const checkIpStatus = useCallback(async () => {
    try {
      const data = await apiService.checkIpStatus();
      if (data.status === 'banned') {
        setStatus('banned');
        setBannedInfo(data.ban_info);
      } else if (data.status === 'locked') {
        setStatus('locked');
        setRetryAfter(data.retry_after);
        setError(data.message);
      } else {
        setStatus('allowed');
      }
    } catch (err) {
      // Serveur injoignable : on laisse tenter plutôt que de bloquer l'accès.
      console.error('Vérification IP impossible :', err);
      setStatus('allowed');
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    checkIpStatus();
  }, [checkIpStatus]);

  /* Décompte du verrouillage serveur */
  useEffect(() => {
    if (retryAfter <= 0) return undefined;
    const timer = setInterval(() => {
      setRetryAfter((prev) => {
        if (prev <= 1) {
          checkIpStatus();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [retryAfter, checkIpStatus]);

  /* Décompte de la limitation de débit (429) */
  useEffect(() => {
    if (countdown <= 0) {
      if (rateLimited) {
        setRateLimited(false);
        setError('');
      }
      return undefined;
    }
    const timer = setTimeout(() => setCountdown((value) => value - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown, rateLimited]);

  const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) return `${h} h ${m} min ${s} s`;
    if (m > 0) return `${m} min ${s} s`;
    return `${s} s`;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (rateLimited || loading) return;

    setLoading(true);
    setError('');

    try {
      const data = await apiService.login(username, password);

      // Un succès sans jeton n'est pas un succès : on ne connecte jamais
      // sur la seule foi du drapeau `success`.
      if (data.success && data.token) {
        localStorage.setItem('cinestream_token', data.token);
        localStorage.setItem('cinestream_user', JSON.stringify(data.user));
        onLoginSuccess(data.user);
      } else {
        setError(data.message || 'Identifiants incorrects');
      }
    } catch (err) {
      console.error('Connexion impossible :', err);
      const code = err.response?.status;

      if (code === 429 || err.message?.includes('429')) {
        const wait = parseInt(err.response?.headers?.['retry-after'], 10) || 300;
        setRateLimited(true);
        setCountdown(wait);
        setError(`Trop de tentatives. Réessayez dans ${Math.ceil(wait / 60)} minutes.`);
      } else if (code === 401) {
        setError('Identifiants incorrects');
      } else if (code === 403) {
        setError(err.response?.data?.message || 'Accès refusé');
      } else {
        setError(err.response?.data?.message || 'Serveur injoignable. Réessayez dans un instant.');
      }
    } finally {
      setLoading(false);
    }
  };

  /* ─────────────────────── Vérification en cours ─────────────────────── */
  if (checking) {
    return (
      <div className="gate">
        <div className="gate-beam" aria-hidden="true" />
        <div className="stack items-center gap-4 relative">
          <span className="gate-spinner anim-spin" aria-hidden="true" />
          <p className="text-[var(--t-low)]">Vérification de sécurité…</p>
        </div>
      </div>
    );
  }

  /* ─────────────────────────── Bannissement ──────────────────────────── */
  if (status === 'banned') {
    return (
      <div className="gate gate-banned">
        <div className="gate-card panel">
          <div className="stack items-center text-center gap-4">
            <span className="gate-mark gate-mark-danger">
              <Ban aria-hidden="true" />
            </span>
            <h1 className="display-md" style={{ color: 'var(--c-curtain-hot)' }}>
              Accès interdit
            </h1>
            <p className="text-[var(--t-mid)]">
              Cette adresse IP a été bannie définitivement.
            </p>
          </div>

          {bannedInfo && (
            <dl className="gate-facts">
              <div>
                <dt>Motif</dt>
                <dd>{bannedInfo.reason}</dd>
              </div>
              <div>
                <dt>Date</dt>
                <dd>{new Date(bannedInfo.banned_at).toLocaleString('fr-FR')}</dd>
              </div>
              {bannedInfo.banned_by && (
                <div>
                  <dt>Par</dt>
                  <dd>
                    {bannedInfo.banned_by === 'system' ? 'Système automatique' : bannedInfo.banned_by}
                  </dd>
                </div>
              )}
            </dl>
          )}

          <p className="foot-note text-center">
            Si vous pensez qu'il s'agit d'une erreur, contactez un administrateur.
          </p>
        </div>
      </div>
    );
  }

  const locked = status === 'locked' && retryAfter > 0;
  const blocked = locked || rateLimited;

  /* ───────────────────────────── Connexion ───────────────────────────── */
  return (
    <div className="gate">
      <div className="gate-beam" aria-hidden="true" />

      <div className="gate-inner">
        {/* Colonne d'accroche — masquée sur mobile pour aller droit au but */}
        <section className="gate-pitch">
          <p className="eyebrow eyebrow-gold mb-4">Séance privée</p>
          <h1 className="display-xl">
            Votre
            <br />
            cinéma.
          </h1>
          <p className="gate-tagline">
            Vos films, vos séries, vos soirées — au même endroit, sans publicité
            et sans compromis sur la qualité.
          </p>
          <div className="gate-strip" aria-hidden="true">
            <div className="reel-strip" />
          </div>
        </section>

        {/* Carte d'accès */}
        <section className="gate-card panel">
          <img className="gate-logo" src="/assets/logoFull720x180-nb.svg" alt="Pop Movie" />

          <form onSubmit={handleSubmit} className="stack gap-4">
            <div>
              <label className="label" htmlFor="gate-user">
                Nom d'utilisateur
              </label>
              <div className="field-wrap">
                <User className="field-icon" aria-hidden="true" />
                <input
                  id="gate-user"
                  className="field"
                  type="text"
                  autoComplete="username"
                  autoCapitalize="none"
                  spellCheck="false"
                  value={username}
                  onChange={(event) => setUsername(event.target.value)}
                  placeholder="Votre identifiant"
                  disabled={blocked || loading}
                  required
                />
              </div>
            </div>

            <div>
              <label className="label" htmlFor="gate-pass">
                Mot de passe
              </label>
              <div className="field-wrap">
                <Lock className="field-icon" aria-hidden="true" />
                <input
                  id="gate-pass"
                  className="field"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="Votre mot de passe"
                  disabled={blocked || loading}
                  required
                />
              </div>
            </div>

            {error && (
              <p className="gate-error" role="alert">
                <AlertCircle aria-hidden="true" />
                <span>{error}</span>
              </p>
            )}

            {blocked && (
              <p className="gate-wait" role="status">
                <Clock aria-hidden="true" />
                <span>
                  Nouvelle tentative possible dans{' '}
                  <b>{formatTime(locked ? retryAfter : countdown)}</b>
                </span>
              </p>
            )}

            <button
              type="submit"
              className="btn btn-lg btn-gold btn-block"
              disabled={blocked || loading}
            >
              {loading ? (
                <>
                  <span className="gate-spinner gate-spinner-sm anim-spin" aria-hidden="true" />
                  Connexion…
                </>
              ) : (
                'Entrer'
              )}
            </button>
          </form>

          <p className="gate-note">
            <Shield aria-hidden="true" />
            Connexion chiffrée · protection contre les tentatives répétées
          </p>
        </section>
      </div>

      <p className="gate-legal">Accès réservé aux membres autorisés</p>
    </div>
  );
}
